/**
 * Slack growth nudge — shows a single sonner toast to logged-in members
 * who haven't joined the Slack community, once they've opened the app
 * on 3 distinct calendar days.
 *
 * Why calendar days instead of raw mount count: hot-reloads, tab
 * duplicates, and quick "check-one-thing" reloads shouldn't burn
 * through the trigger. Counting unique yyyy-mm-dd bumps is a decent
 * proxy for "third real visit".
 *
 * State in localStorage:
 *   slack-nudge-days   → comma-separated ISO dates (max 30 kept)
 *   slack-nudge-shown  → "1" once the toast has fired (never fires
 *                        again, even if they dismiss without joining;
 *                        we don't want to nag)
 *
 * Rendered as a top-level side-effect component (no DOM) inside the
 * shell so it runs on every route.
 */
import { useEffect } from "react";
import { toast } from "sonner";
import { hasJoinedSlack, SLACK_INVITE_URL, markSlackJoined } from "../lib/slack";
import { useAuth } from "../context/AuthContext";

const DAYS_KEY = "slack-nudge-days";
const SHOWN_KEY = "slack-nudge-shown";
const THRESHOLD_DAYS = 3;

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function recordVisitDay() {
  try {
    const raw = window.localStorage.getItem(DAYS_KEY) || "";
    const days = new Set(raw.split(",").filter(Boolean));
    days.add(todayIso());
    // Trim to last 30 entries so the string never grows unbounded.
    const arr = Array.from(days).sort().slice(-30);
    window.localStorage.setItem(DAYS_KEY, arr.join(","));
    return arr.length;
  } catch {
    return 0;
  }
}

function markShown() {
  try {
    window.localStorage.setItem(SHOWN_KEY, "1");
  } catch { /* noop */ }
}

function alreadyShown() {
  try {
    return window.localStorage.getItem(SHOWN_KEY) === "1";
  } catch {
    return false;
  }
}

export default function SlackNudge() {
  const { user } = useAuth();
  useEffect(() => {
    // Only nudge logged-in members and only if we have an invite URL
    // configured. If the founder is logged out, they can't join Slack
    // through our workspace anyway — the nudge would be noise.
    if (!user) return;
    if (!SLACK_INVITE_URL) return;
    if (hasJoinedSlack()) return;
    if (alreadyShown()) return;

    const dayCount = recordVisitDay();
    if (dayCount < THRESHOLD_DAYS) return;

    // Small delay so the toast lands after the initial route paint
    // instead of racing with page-load spinners.
    const timer = setTimeout(() => {
      // Double-check right before firing — the flag could have flipped
      // during navigation between mount and the timeout resolving.
      if (hasJoinedSlack() || alreadyShown()) return;
      markShown();
      toast("Join founders in our Slack", {
        description:
          "You've been here a few times now — swap notes with the rest of the community.",
        duration: 12000,
        action: {
          label: "Join Slack",
          onClick: () => {
            markSlackJoined();
            window.open(SLACK_INVITE_URL, "_blank", "noopener,noreferrer");
          },
        },
        cancel: { label: "Later", onClick: () => {} },
      });
    }, 1200);

    return () => clearTimeout(timer);
  }, [user]);

  return null;
}
